// Klaviyo client subscriptions: public site ID only; the list controls double opt-in.
if (!customElements.get('footer-newsletter')) {
  customElements.define('footer-newsletter', class extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('form');
      this.email = this.form.querySelector('input[type="email"]');
      this.button = this.form.querySelector('button[type="submit"]');
      this.status = this.querySelector('[data-newsletter-status]');
      this.acceptedEmails = new Set();
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.button.disabled = false;
    }

    getSiteId() {
      // Shopify's enabled Klaviyo app embed already supplies the account's site ID.
      for (const script of document.querySelectorAll('script[src]')) {
        const url = new URL(script.getAttribute('src'), document.baseURI);
        if (url.hostname !== 'static.klaviyo.com' || !url.pathname.startsWith('/onsite/js/')) continue;
        const siteId = url.searchParams.get('company_id') || url.pathname.split('/')[3];
        if (/^[a-zA-Z0-9]{6}$/.test(siteId)) return siteId;
      }
      const siteId = this.dataset.siteId.trim();
      return /^[a-zA-Z0-9]{6}$/.test(siteId) ? siteId : null;
    }

    showStatus(message, isError = false) {
      this.status.textContent = message;
      this.status.hidden = false;
      this.status.setAttribute('role', isError ? 'alert' : 'status');
      this.status.setAttribute('aria-live', isError ? 'assertive' : 'polite');
    }

    async submit(event) {
      event.preventDefault();
      if (this.pending) return;
      this.email.value = this.email.value.trim();
      if (!this.form.reportValidity()) return;

      const email = this.email.value;
      const emailKey = email.toLowerCase();
      const successMessage = 'Thank you! Your subscription request was accepted. If a confirmation email arrives, please confirm to complete your subscription.';
      if (this.acceptedEmails.has(emailKey)) {
        this.showStatus(successMessage);
        return;
      }

      const siteId = this.getSiteId();
      if (!siteId) {
        this.showStatus('Newsletter signup is temporarily unavailable. Please try again later.', true);
        return;
      }

      this.pending = true;
      this.button.disabled = true;
      this.email.readOnly = true;
      this.form.setAttribute('aria-busy', 'true');
      this.showStatus('Submitting…');
      const controller = new AbortController();
      const timeout = setTimeout(() => controller.abort(), 15000);

      try {
        const response = await fetch(`https://a.klaviyo.com/client/subscriptions?company_id=${encodeURIComponent(siteId)}`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/vnd.api+json',
            'Accept': 'application/vnd.api+json',
            'revision': '2026-07-15'
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Shopify footer newsletter',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email,
                      subscriptions: { email: { marketing: { consent: 'SUBSCRIBED' } } }
                    }
                  }
                }
              },
              relationships: { list: { data: { type: 'list', id: this.dataset.listId } } }
            }
          }),
          signal: controller.signal
        });
        // A 202 confirms acceptance, not completion of any required double opt-in.
        if (response.status !== 202) {
          this.showStatus(response.status === 429
            ? 'Too many signup attempts. Please wait a moment and try again.'
            : 'We could not submit your subscription request. Please check your email address and try again.', true);
          return;
        }
        this.acceptedEmails.add(emailKey);
        this.showStatus(successMessage);
      } catch (error) {
        this.showStatus('We could not confirm your subscription request. Please check your connection and try again.', true);
      } finally {
        clearTimeout(timeout);
        this.pending = false;
        this.button.disabled = false;
        this.email.readOnly = false;
        this.form.removeAttribute('aria-busy');
      }
    }
  });
}
